import type { FC, JSX, ReactNode } from "react";
import React, { createContext, useContext } from "react";

import { useBrand } from "../brand";
import type { Brands } from "../brand/types";
import { useCurrencyFormat } from "../currency/CurrencyFormatProvider";
import { useCurrencyLocale } from "../currency/CurrencyLocaleProvider";
import { useDateLocale } from "../date/DateLocaleProvider";
import { ErrorBoundary, ErrorNamespaceProvider } from "../error_handling";
import { DatameshNamespaceProvider } from "../html_event";
import TranslationsNamespaceProvider from "../translations/TranslationsNamespaceProvider";
import type { CurrencyFormat } from "../types";

export const aliases = [];

const NamespaceContext = createContext<string>("");

export const useNamespace = () => useContext(NamespaceContext);
interface DateFnsLocale {
  code: string;
}

interface PrismaProviderProps {
  brand: Brands;
  version: "bluestone" | "cobalt";
  currencyLocale: {
    locale: string;
    minimumFractionDigits?: number;
    numberLocale?: string;
  };
  currencyFormat?: CurrencyFormat;
  dateFnsLocale: DateFnsLocale;
  children: ReactNode;
}

export const withModuleContext = ({
  namespace,
  PrismaProvider,
}: {
  namespace: string;
  PrismaProvider: FC<PrismaProviderProps>;
}) => {
  return <TProps,>(Component: (props: TProps) => ReactNode) =>
    (props: TProps & JSX.IntrinsicAttributes) => {
      const brand = useBrand();
      const currencyLocale = useCurrencyLocale();
      const currencyFormat = useCurrencyFormat();
      const dateLocale = useDateLocale();
      return (
        <PrismaProvider
          brand={brand}
          version="cobalt"
          currencyLocale={currencyLocale}
          dateFnsLocale={dateLocale}
          currencyFormat={currencyFormat}
        >
          <NamespaceContext.Provider value={namespace}>
            <DatameshNamespaceProvider namespace={namespace}>
              <TranslationsNamespaceProvider namespace={namespace}>
                <ErrorNamespaceProvider namespace={namespace}>
                  <ErrorBoundary name="default_library_error">
                    <Component {...props} />
                  </ErrorBoundary>
                </ErrorNamespaceProvider>
              </TranslationsNamespaceProvider>
            </DatameshNamespaceProvider>
          </NamespaceContext.Provider>
        </PrismaProvider>
      );
    };
};
